/**
 * Record WhatsApp Chat Component
 * Shows WhatsApp chat for a specific record (Account, Opportunity, WorkOrder, etc.)
 * Groups messages by date and filters by current user
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import config from '../../config/config';
// Phase 1.1 chat shared components (owner-spec 2026-05-31): bring the
// record-scoped chat to parity with the two omnichannel views.
import { renderMedia, buildMediaList, buildReplyTarget } from './chat/MessageBubble';
import MediaLightbox from './chat/MediaLightbox';
import MediaLibraryDrawer from './chat/MediaLibraryDrawer';
import ChatSearchBar from './chat/ChatSearchBar';
import ReplyQuote from './chat/ReplyQuote';
import ReplyComposerBar from './chat/ReplyComposerBar';
// Phase 3 chat action menu + info modal (owner-spec 2026-05-31).
import MessageActionsMenu from './chat/MessageActionsMenu';
import MessageInfoModal from './chat/MessageInfoModal';
// One reader for both stored timestamp encodings. The local helper this
// replaced did parseInt() on a string, which sent every ISO-8601 inbound
// message to 1 January 1970 — see utils/waMessageTime.js.
import {
  toEpochMs, formatTimeHe, formatDateTimeHe, hoursSince, groupMessagesByDate,
} from './utils/waMessageTime';

// Use main API server which proxies to WhatsApp server
const API_BASE = config.api.baseUrl;
const WHATSAPP_API = API_BASE; // Now goes through main server proxy

// Message templates — owner-spec 2026-06-16: was a hardcoded constant.
// Now fetched live from /api/whatsapp/quick-templates/active; admin tab at
// 'הגדרות → תבניות הודעות WhatsApp' is the single source of truth.
// Server templates carry { id, name, text, isActive }; renderTemplate
// substitutes {userName} into the body at click time.
const renderTemplate = (rawText, userName) =>
  String(rawText || '').replace(/\{userName\}/g, userName || '');

// Quick emojis
const QUICK_EMOJIS = ['😊', '👍', '🙏', '✅', '❤️', '👋', '📞', '📅', '🔧', '💧', '🚗', '⏰', '📋', '💳', '🏠', '☀️', '🌟', '💙', '😃', '👌'];

/**
 * ⭐ 2026-09-04, Baileys removal stage 2. The Node/Baileys service died on
 * 2026-07-06; these two calls have been failing ever since. Guarded rather
 * than deleted, matching stage 1 on the backend — stage 3 removes both sides
 * and `WHATSAPP_USE_BAILEYS` together. A named constant, not a literal false,
 * so eslint does not read the code below as unreachable and fail the build.
 */
const BAILEYS_ALIVE = false;

const RecordWhatsAppChat = ({ currentRecord, objectName, currentUser, initialPhone }) => {
  const [phone, setPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showEmojis, setShowEmojis] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [accountData, setAccountData] = useState(null);
  // New states for conversation info
  const [lastConversationInfo, setLastConversationInfo] = useState(null);
  const [isWithin24Hours, setIsWithin24Hours] = useState(true); // Default true — no template restriction (Baileys, not Business API)
  const [lastCustomerMessage, setLastCustomerMessage] = useState(null);
  // Phone picker states
  const [allPhones, setAllPhones] = useState([]); // [{field, value, source, hasWhatsApp, jid}]
  const [selectedPhoneIndex, setSelectedPhoneIndex] = useState(0);

  // Phase 1.1 (owner-spec 2026-05-31) — chat-level UI state for media
  // library, lightbox, and in-chat search. Mirror of WhatsAppChat.js /
  // ChatModal so the same shared components work here.
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [chatSearchMatchIdx, setChatSearchMatchIdx] = useState(0);
  const messageRefs = useRef({});

  const lightboxMedia = useMemo(() => buildMediaList(messages), [messages]);
  // Search matches keyed by msg.id (stable identifier, works regardless
  // of date-grouping the render does). Owner-flagged 2026-05-31 that
  // the arrows only updated the counter — fixed below via scroll effect.
  const chatSearchMatches = useMemo(() => {
    const q = (chatSearchQuery || '').trim().toLowerCase();
    if (!q) return [];
    return messages
      .filter(m => (m.body || '').toLowerCase().includes(q))
      .map(m => m.id)
      .filter(Boolean);
  }, [messages, chatSearchQuery]);

  useEffect(() => { setChatSearchMatchIdx(0); }, [chatSearchMatches.length]);

  useEffect(() => {
    if (!chatSearchMatches.length) return;
    const targetId = chatSearchMatches[chatSearchMatchIdx];
    const el = messageRefs.current[targetId];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [chatSearchMatchIdx, chatSearchMatches]);

  const openLightboxForMsg = useCallback((msg) => {
    const idx = lightboxMedia.findIndex(it => it.id === msg.id);
    setLightbox({ open: true, index: Math.max(0, idx) });
  }, [lightboxMedia]);

  // Phase 2 (owner-spec 2026-05-31): reply state.
  const [replyTarget, setReplyTarget] = useState(null);
  // Phase 3: message-info modal.
  const [infoModalMsg, setInfoModalMsg] = useState(null);
  const scrollToMessage = useCallback((id) => {
    const el = messageRefs.current && messageRefs.current[id];
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);
  const [smsOnlyMode, setSmsOnlyMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [endingChat, setEndingChat] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  // Owner-flagged 2026-05-31: poll closure + sticky-scroll fixes.
  // phoneRef tracks the live phone so the poll setInterval below
  // doesn't keep calling loadMessages with a stale value when the
  // user picks a different phone from the picker.
  const phoneRef = useRef(phone);
  useEffect(() => { phoneRef.current = phone; }, [phone]);
  // The scroll container for sticky-bottom behaviour.
  const messagesAreaRef = useRef(null);
  // Tracks whether the FIRST batch of messages for the current chat
  // has already been auto-scrolled to bottom. Reset on chat switch.
  // Without this, the initial render lands with scrollTop=0 + a long
  // scrollHeight → `nearBottom` returns false → no scroll → user sees
  // the OLDEST message at the top (owner-flagged 2026-05-31).
  const didInitialScrollRef = useRef(false);
  useEffect(() => {
    // New chat opened → arm the initial-scroll for the next messages
    // arrival.
    didInitialScrollRef.current = false;
  }, [phone]);

  // Fetch Account data if we have AccountId
  const fetchAccountData = useCallback(async (accountId) => {
    if (!accountId) return null;
    try {
      const res = await fetch(`${API_BASE}/api/objects/Account/records/${accountId}`);
      const data = await res.json();
      if (data.record) {
        setAccountData(data.record);
        return data.record;
      }
    } catch (err) {
      console.error('Error fetching account:', err);
    }
    return null;
  }, []);

  // Find ALL phone numbers from record + linked Account (no Fax)
  const findAllPhoneNumbers = useCallback((record, account) => {
    if (!record) return [];

    const phoneFields = [
      'Phone', 'PersonMobilePhone', 'MobilePhone',
      'Another_phone__c', 'Another_Phone__c',
      'OtherPhone', 'PersonOtherPhone', 'HomePhone'
    ];

    const found = [];
    const seen = new Set(); // deduplicate by normalized value

    const addPhone = (field, value, source) => {
      if (!value) return;
      const cleaned = String(value).replace(/[^0-9]/g, '');
      if (cleaned.length < 7) return; // too short
      if (seen.has(cleaned)) return;
      seen.add(cleaned);
      found.push({ field, value: String(value), source });
    };

    // From the record itself
    for (const field of phoneFields) {
      if (record[field]) addPhone(field, record[field], objectName);
    }

    // From nested Account
    if (record.Account) {
      for (const field of phoneFields) {
        if (record.Account[field]) addPhone(field, record.Account[field], 'Account');
      }
    }

    // From fetched account data
    if (account) {
      for (const field of phoneFields) {
        if (account[field]) addPhone(field, account[field], 'Account');
      }
    }

    return found;
  }, [objectName]);

  // Get customer name from record or Account
  const getCustomerName = useCallback((record, account) => {
    if (!record) return '';

    // For Account objects, use Name directly
    if (objectName === 'Account') {
      return record.Name || '';
    }

    // For other objects, try Account name first
    if (record.Account?.Name) {
      return record.Account.Name;
    }
    if (account?.Name) {
      return account.Name;
    }

    // Fallback to record Name or FirstName/LastName
    return record.Name ||
           `${record.FirstName || ''} ${record.LastName || ''}`.trim() ||
           '';
  }, [objectName]);

  // Check WhatsApp connection status
  const checkConnection = useCallback(async () => {
    try {
      // stage 2: the Baileys connection status — the service is gone.
      if (!BAILEYS_ALIVE) return;
      const res = await fetch(`${WHATSAPP_API}/api/whatsapp/status`);
      const data = await res.json();
      // Server returns status: "connected" or isConnected: true
      setWhatsappConnected(data.isConnected || data.status === 'connected' || data.isReady);
    } catch (err) {
      console.error('WhatsApp connection check failed:', err);
      setWhatsappConnected(false);
    }
  }, []);

  // Convert Israeli phone number to international format
  const normalizePhone = (phone) => {
    if (!phone) return null;
    // Remove all non-digits
    let cleaned = phone.replace(/[^0-9]/g, '');

    // Convert Israeli format (05x) to international (9725x)
    if (cleaned.startsWith('05') && cleaned.length === 10) {
      cleaned = '972' + cleaned.substring(1);
    }
    // If starts with 0 and 9 digits, add 972
    else if (cleaned.startsWith('0') && cleaned.length === 9) {
      cleaned = '972' + cleaned.substring(1);
    }
    // If doesn't start with country code, assume Israel
    else if (!cleaned.startsWith('972') && cleaned.length === 9) {
      cleaned = '972' + cleaned;
    }

    return cleaned;
  };

  // Load messages for phone number - show ALL messages for this customer.
  // Owner-flagged 2026-05-31: every 3s poll set `loading=true`, which
  // swapped the message list for a "טוען הודעות..." placeholder for one
  // render tick. That collapsed scrollHeight to ~0, the browser reset
  // scrollTop, and when the messages re-rendered the user was at the
  // TOP of history — and could feel the viewport "jump" while trying
  // to scroll. New behaviour: only show the loading spinner the FIRST
  // time we load for a phone (silent param defaults to opposite).
  const loadMessages = useCallback(async (phoneNumber, opts = {}) => {
    if (!phoneNumber) return;
    const silent = !!opts.silent;
    if (!silent) setLoading(true);
    setError(null);
    try {
      // Normalize phone number to international format
      const normalizedPhone = normalizePhone(phoneNumber);
      const chatId = normalizedPhone + '@s.whatsapp.net';

      console.log('[WhatsApp] Loading messages for:', chatId, '(original:', phoneNumber, ')');

      const res = await fetch(`${WHATSAPP_API}/api/whatsapp/messages/${encodeURIComponent(chatId)}`);
      const data = await res.json();

      console.log('[WhatsApp] Messages response:', data);

      if (data.messages && Array.isArray(data.messages)) {
        // Show ALL messages for this customer - don't filter by user
        // This way all employees see the full conversation history
        setMessages(data.messages);

        // Calculate 24-hour window and last conversation info
        const msgs = data.messages;
        if (msgs.length > 0) {
          // Find last customer message (incoming)
          const customerMessages = msgs.filter(m => !m.fromMe);
          if (customerMessages.length > 0) {
            const lastCustomerMsg = customerMessages[customerMessages.length - 1];
            setLastCustomerMessage(lastCustomerMsg);

            // Check if within 24 hours. An unreadable timestamp is not
            // evidence the window is open, so it closes the window rather
            // than inheriting a fabricated "now".
            const hoursDiff = hoursSince(lastCustomerMsg.timestamp);
            setIsWithin24Hours(hoursDiff != null && hoursDiff <= 24);
          }

          // Find last conversation info (last outgoing message from agent)
          const agentMessages = msgs.filter(m => m.fromMe && m.sentByName);
          if (agentMessages.length > 0) {
            const lastAgentMsg = agentMessages[agentMessages.length - 1];
            setLastConversationInfo({
              date: toEpochMs(lastAgentMsg.timestamp),
              agentName: lastAgentMsg.sentByName || 'נציג',
              conversationId: data.conversationId // If available from API
            });
          }
        }
      } else {
        setMessages([]);
        setLastCustomerMessage(null);
        setIsWithin24Hours(false);
        setLastConversationInfo(null);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('שגיאה בטעינת הודעות');
      setMessages([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Check WhatsApp registration for phone numbers
  const checkPhoneNumbers = useCallback(async (phones) => {
    if (!phones || phones.length === 0) return phones;
    try {
      const res = await fetch(`${WHATSAPP_API}/api/whatsapp/check-number`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phones: phones.map(p => p.value) })
      });
      const data = await res.json();
      if (data.results) {
        return phones.map((p, i) => ({
          ...p,
          hasWhatsApp: data.results[i]?.hasWhatsApp ?? null,
          jid: data.results[i]?.jid || null,
          normalizedPhone: data.results[i]?.normalizedPhone || null
        }));
      }
    } catch (err) {
      console.error('[WhatsApp] check-number failed:', err);
    }
    return phones.map(p => ({ ...p, hasWhatsApp: null }));
  }, []);

  // Initialize - find phone and load messages
  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      if (!isMounted) return;
      setLoading(true);

      // Check WhatsApp connection
      await checkConnection();

      // Get AccountId for fetching account data
      const accountId = currentRecord?.AccountId ||
                       (objectName === 'Account' ? currentRecord?.Id : null);

      // Fetch account data if needed
      let account = null;
      if (accountId && objectName !== 'Account') {
        try {
          const res = await fetch(`${API_BASE}/api/objects/Account/records/${accountId}`);
          const data = await res.json();
          if (data.record && isMounted) {
            account = data.record;
            setAccountData(account);
          }
        } catch (err) {
          console.error('Error fetching account:', err);
        }
      }

      // Find ALL phone numbers from record + account
      let phones = findAllPhoneNumbers(currentRecord, account);
      console.log('[WhatsApp] Found phones:', phones.map(p => p.value));

      // Check which have WhatsApp
      if (phones.length > 0) {
        phones = await checkPhoneNumbers(phones);
      }

      if (!isMounted) return;

      setAllPhones(phones);
      setCustomerName(getCustomerName(currentRecord, account));

      // Auto-select phone: use initialPhone if provided, or first WhatsApp-enabled, or first phone
      let selectedIdx = 0;
      if (initialPhone) {
        const initCleaned = String(initialPhone).replace(/[^0-9]/g, '');
        const matchIdx = phones.findIndex(p => p.value.replace(/[^0-9]/g, '') === initCleaned);
        if (matchIdx >= 0) selectedIdx = matchIdx;
      } else {
        // Prefer WhatsApp-enabled phone
        const waIdx = phones.findIndex(p => p.hasWhatsApp === true);
        if (waIdx >= 0) selectedIdx = waIdx;
      }

      setSelectedPhoneIndex(selectedIdx);
      const selectedPhone = phones[selectedIdx]?.value || null;
      setPhone(selectedPhone);

      // Determine if SMS-only mode
      const selectedHasWA = phones[selectedIdx]?.hasWhatsApp;
      setSmsOnlyMode(selectedHasWA === false);

      if (selectedPhone) {
        await loadMessages(selectedPhone);
      } else {
        if (isMounted) setLoading(false);
      }
    };

    initialize();

    // Register as agent
    if (currentUser?.email) {
      // stage 2: registering with a queue dead since 2026-07-06.
      if (!BAILEYS_ALIVE) return;
      fetch(`${WHATSAPP_API}/api/whatsapp/agent/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: currentUser.email,
          agentName: currentUser.name || currentUser.firstName || currentUser.email
        })
      }).catch(() => {});
    }

    // Polling for real-time updates (every 3 seconds).
    // Owner-reported 2026-05-31: messages didn't appear until manual
    // refresh. Two fixes here vs the previous version:
    //   1. Read `phone` via ref so the setInterval closure always sees
    //      the current value (the previous code captured `phone` at the
    //      effect-creation time; if the user switched phones via the
    //      picker the polling kept calling loadMessages with the old
    //      value forever, so new messages on the active phone never
    //      arrived between full reloads).
    //   2. 10s → 3s. WhatsApp Web feels instant; 10s was perceptually
    //      "stuck".
    const pollInterval = setInterval(() => {
      // 2026-06-21 — Page Visibility guard. Don't poll when tab not visible.
      if (typeof document !== 'undefined' && document.hidden) {
        if (typeof window !== 'undefined') {
          window.__pollSkipCount = (window.__pollSkipCount || 0) + 1;
        }
        return;
      }
      const livePhone = phoneRef.current;
      if (isMounted && livePhone) {
        // silent=true → do NOT flip `loading` on/off so the message
        // list stays mounted; the scroll container keeps its scrollTop
        // and the user doesn't feel a "jump" on each 3s poll.
        loadMessages(livePhone, { silent: true });
      }
    }, 3000);

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRecord?.Id, objectName]);

  // Sticky scroll-to-bottom. Two modes:
  //   - FIRST messages-load for this chat: force to bottom regardless
  //     of where scrollTop happens to be (browser default is 0 + a
  //     long scrollHeight, so the sticky check would otherwise say
  //     "user is far from the bottom, don't disturb" → leaves the user
  //     staring at the oldest message).
  //   - Subsequent updates: only auto-scroll when user is at/near
  //     the bottom (so reading history doesn't get yanked on every
  //     3s poll).
  // Uses scrollTop = scrollHeight directly — no smooth animation, so
  // the viewport doesn't visibly pan across the whole conversation.
  useEffect(() => {
    const container = messagesAreaRef.current;
    if (!container || messages.length === 0) return;
    if (!didInitialScrollRef.current) {
      container.scrollTop = container.scrollHeight;
      didInitialScrollRef.current = true;
      return;
    }
    const nearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 120;
    if (nearBottom) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // Send message
  const handleSendMessage = async (channel = 'whatsapp') => {
    if (!messageText.trim() || !phone) return;

    // Note: 24-hour template restriction removed — using Baileys (WhatsApp Web), not Business API

    const endpoint = channel === 'sms' ? '/api/sms/send' : '/api/whatsapp/send';

    setSending(true);
    try {
      // Normalize phone before sending
      const normalizedPhone = normalizePhone(phone);

      const res = await fetch(`${WHATSAPP_API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: normalizedPhone,
          message: messageText,
          userId: currentUser?.email,
          userName: currentUser?.name || currentUser?.firstName,
          source: 'record_tab',
          recordId: currentRecord?.Id,
          recordType: objectName,
          // Phase 2 owner-spec 2026-05-31.
          replyTo: replyTarget || undefined,
        })
      });

      const data = await res.json();
      if (data.success) {
        setMessageText('');
        setReplyTarget(null);
        // silent=true so the post-send refresh doesn't unmount the
        // message list and reset scrollTop (owner-flagged 2026-05-31).
        loadMessages(phone, { silent: true });
      } else {
        setError(data.error || 'שגיאה בשליחה');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  // End chat — log a SYSTEM marker locally + return customer to bot.
  // PR #795 (#4) 2026-06-08 + PR #800 (#4/#5 v2 2026-06-08) owner-spec:
  //   - The "user ended session at date+time" line is a SYSTEM-LEVEL
  //     annotation that should appear in the chat panel as a centered
  //     date-style badge — it is NOT sent to the customer's WhatsApp.
  //   - Calls /api/whatsapp/chat-system-marker (local save only) instead
  //     of /api/whatsapp/send (which would actually fire Infobip).
  //   - Still calls /api/whatsapp/bot-session/{phone}/return-to-bot so
  //     the customer's NEXT message goes back through the bot.
  const handleEndChat = async () => {
    if (!phone || endingChat) return;
    setEndingChat(true);
    try {
      const userName = currentUser?.name || currentUser?.firstName || currentUser?.email || 'משתמש';
      const now = new Date();
      const dateStr = now.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const timeStr = now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
      const endMessage = `${userName} סיים את השיחה בתאריך: ${dateStr} ${timeStr}`;

      const normalizedPhone = normalizePhone(phone);
      const res = await fetch(`${WHATSAPP_API}/api/whatsapp/chat-system-marker`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: normalizedPhone,
          message: endMessage,
          userId: currentUser?.email,
          userName: userName,
        })
      });
      const data = await res.json();
      if (data.success) {
        try {
          await fetch(`/api/whatsapp/bot-session/${encodeURIComponent(normalizedPhone)}/return-to-bot`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
        } catch (rtbErr) {
          console.warn('[WA EndChat] return-to-bot failed (marker still saved):', rtbErr);
        }
        // silent=true so the post-send refresh doesn't unmount the
        // message list and reset scrollTop (owner-flagged 2026-05-31).
        loadMessages(phone, { silent: true });
      } else {
        setError(data.error || 'שגיאה בסיום שיחה');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setEndingChat(false);
    }
  };

  // Send media
  const handleSendMedia = async (file, type) => {
    if (!phone || !file) return;

    setUploadingFile(true);
    setShowAttachMenu(false);

    try {
      // Normalize phone before sending
      const normalizedPhone = normalizePhone(phone);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('phone', normalizedPhone);
      formData.append('userId', currentUser?.email);
      formData.append('userName', currentUser?.name || currentUser?.firstName);
      formData.append('type', type);
      // Phase 2 owner-spec 2026-05-31: reply context for media.
      if (replyTarget) formData.append('replyTo', JSON.stringify(replyTarget));

      const res = await fetch(`${WHATSAPP_API}/api/whatsapp/send-media`, {
        method: 'POST',
        body: formData
      });

      const data = await res.json();
      if (data.success) {
        setReplyTarget(null);
        // silent=true so the post-send refresh doesn't unmount the
        // message list and reset scrollTop (owner-flagged 2026-05-31).
        loadMessages(phone, { silent: true });
      } else {
        setError(data.error || 'שגיאה בהעלאה');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      handleSendMedia(file, type);
    }
    e.target.value = '';
  };

  const handleSelectTemplate = (template) => {
    const userName = currentUser?.firstName || currentUser?.name || 'נציג/ה';
    const text = typeof template.getTemplate === 'function'
      ? template.getTemplate(userName)
      : renderTemplate(template.text, userName);
    setMessageText(text);
    setShowTemplates(false);
  };

  // 2026-06-16: fetch active templates each time the picker opens.
  useEffect(() => {
    if (!showTemplates) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/whatsapp/quick-templates/active`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setTemplates(data.templates || []);
      } catch (e) { /* keep prior list */ }
    })();
    return () => { cancelled = true; };
  }, [showTemplates]);

  const handleSelectEmoji = (emoji) => {
    setMessageText(prev => prev + emoji);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Group messages by date
  const formatTime = (timestamp) => formatTimeHe(timestamp);

  const getStatusIcon = (msg) => {
    if (!msg.fromMe) return null;
    switch (msg.ack) {
      case 0: return '⏳';
      case 1: return '✓';
      case 2: return '✓✓';
      case 3: return <span style={{ color: '#34b7f1' }}>✓✓</span>;
      default: return '✓';
    }
  };

  // Phase 1.1 (owner-spec 2026-05-31) — delegate to the shared
  // `renderMedia` dispatcher (image / video / audio / voice / document)
  // with proper loading + broken states, identical to the other two
  // chat consumers. Image click goes through the lightbox.
  const renderMessageContent = (msg) => {
    const t = (msg.mediaType || msg.type || '').toLowerCase();
    if (['image', 'video', 'audio', 'voice', 'document'].includes(t)) {
      return renderMedia(msg, openLightboxForMsg);
    }
    return <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.body}</div>;
  };

  const messageGroups = groupMessagesByDate(messages);

  // Handle phone selection from picker
  const handlePhoneSelect = (idx) => {
    const selected = allPhones[idx];
    if (!selected) return;
    setSelectedPhoneIndex(idx);
    setPhone(selected.value);
    setSmsOnlyMode(selected.hasWhatsApp === false);
    loadMessages(selected.value);
  };

  // No phone found
  if (!phone && !loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📱</span>
        <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600' }}>לא נמצא מספר טלפון</h3>
        <p style={{ margin: 0, fontSize: '14px' }}>אין מספר טלפון ברשומה זו או בחשבון המקושר</p>
      </div>
    );
  }

  // Not connected — allow SMS-only mode instead of blocking
  const canSendSms = true; // Infobip always available as fallback

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', direction: 'rtl',
      ...(isFullscreen ? {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
        height: '100vh', width: '100vw', borderRadius: 0,
      } : {
        height: '600px',
      })
    }}>
      {/* Header - Compact */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        background: (!whatsappConnected && !smsOnlyMode) ? 'linear-gradient(135deg, #5856D6 0%, #3634A3 100%)' : smsOnlyMode ? 'linear-gradient(135deg, #5856D6 0%, #3634A3 100%)' : 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
        borderRadius: isFullscreen ? '0' : '8px 8px 0 0',
        color: 'white',
        minHeight: '36px'
      }}>
        <div style={{
          width: '28px',
          height: '28px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '13px',
          flexShrink: 0
        }}>
          {customerName?.[0]?.toUpperCase() || '👤'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: '600', fontSize: '13px' }}>{customerName || 'לקוח'}</span>
          <span style={{ fontSize: '12px', opacity: 0.8, marginRight: '8px' }}>{phone}</span>
        </div>
        {/* Channel status */}
        <div style={{
          padding: '2px 8px',
          borderRadius: '10px',
          fontSize: '10px',
          fontWeight: '600',
          background: 'rgba(255,255,255,0.25)',
          color: 'white',
          flexShrink: 0
        }}>
          {(!whatsappConnected || smsOnlyMode) ? 'SMS' : 'WhatsApp'}
        </div>
        {/* Phase 1.1 owner-spec 2026-05-31: in-chat search + media library */}
        <button
          onClick={() => setChatSearchOpen(s => !s)}
          title="חיפוש בצ'אט"
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            padding: '3px 8px',
            fontSize: '14px',
            color: 'white',
            flexShrink: 0,
            lineHeight: 1,
          }}
        >🔍</button>
        <button
          onClick={() => setLibraryOpen(true)}
          title="מדיה, מסמכים וקישורים"
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            padding: '3px 8px',
            fontSize: '14px',
            color: 'white',
            flexShrink: 0,
            lineHeight: 1,
          }}
        >📁</button>
        {/* End Chat button */}
        <button
          onClick={handleEndChat}
          disabled={endingChat}
          title="סיום שיחה"
          style={{
            background: endingChat ? 'rgba(255,255,255,0.15)' : 'rgba(255,80,80,0.85)',
            border: 'none',
            borderRadius: '14px',
            padding: '4px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: endingChat ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            color: 'white',
            flexShrink: 0,
            opacity: endingChat ? 0.6 : 1
          }}
        >
          {endingChat ? '...' : 'סיום שיחה'}
        </button>
        {/* Fullscreen toggle */}
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          title={isFullscreen ? 'צא ממסך מלא' : 'מסך מלא'}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            padding: '3px 6px',
            fontSize: '14px',
            color: 'white',
            flexShrink: 0,
            lineHeight: 1
          }}
        >
          {isFullscreen ? '✕' : '⛶'}
        </button>
      </div>

      {/* Phase 1.1 owner-spec 2026-05-31: in-chat search bar */}
      <ChatSearchBar
        open={chatSearchOpen}
        query={chatSearchQuery}
        onQueryChange={setChatSearchQuery}
        onClose={() => { setChatSearchOpen(false); setChatSearchQuery(''); }}
        matchCount={chatSearchMatches.length}
        currentMatch={chatSearchMatches.length ? chatSearchMatchIdx + 1 : 0}
        onPrev={() => setChatSearchMatchIdx(i => Math.max(0, i - 1))}
        onNext={() => setChatSearchMatchIdx(i => Math.min(chatSearchMatches.length - 1, i + 1))}
      />

      {/* Phone Picker - shows when multiple phones found */}
      {allPhones.length > 1 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '8px 12px',
          background: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <span style={{ fontSize: '12px', color: '#666', alignSelf: 'center', marginLeft: '4px' }}>מספרים:</span>
          {allPhones.map((p, idx) => (
            <button
              key={idx}
              onClick={() => handlePhoneSelect(idx)}
              style={{
                padding: '4px 12px',
                borderRadius: '16px',
                border: idx === selectedPhoneIndex ? '2px solid #1976d2' : '1px solid #ccc',
                background: p.hasWhatsApp === true ? '#e8f5e9' : p.hasWhatsApp === false ? '#f3e5f5' : '#fff',
                color: '#333',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: idx === selectedPhoneIndex ? '600' : '400'
              }}
              title={`${p.field} (${p.source})`}
            >
              <span style={{ fontSize: '10px' }}>
                {p.hasWhatsApp === true ? '🟢' : p.hasWhatsApp === false ? '🟣' : '⚪'}
              </span>
              {p.value}
              {p.hasWhatsApp === false && <span style={{ fontSize: '10px', color: '#7b1fa2' }}>(SMS)</span>}
            </button>
          ))}
        </div>
      )}

      {/* Conversation Info Banner - Compact */}
      {(lastConversationInfo || lastCustomerMessage) && (
        <div style={{
          padding: '4px 12px',
          background: isWithin24Hours ? '#e8f5e9' : '#fff3e0',
          borderBottom: '1px solid #e0e0e0',
          fontSize: '11px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span style={{
            padding: '1px 6px',
            borderRadius: '8px',
            fontSize: '10px',
            fontWeight: '600',
            background: isWithin24Hours ? '#4CAF50' : '#ff9800',
            color: 'white'
          }}>
            {isWithin24Hours ? '✓ בתוך 24 שעות' : '⏰ מעבר ל-24 שעות'}
          </span>

          {lastConversationInfo && (
            <>
              <span style={{ color: '#555' }}>💬 {new Date(lastConversationInfo.date).toLocaleDateString('he-IL', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              <span style={{ color: '#555' }}>👤 <strong style={{ color: '#128c7e' }}>{lastConversationInfo.agentName}</strong></span>
            </>
          )}

          {/* Last customer message - inline */}
          {lastCustomerMessage && (
            <span style={{
              padding: '2px 8px',
              background: 'white',
              borderRadius: '6px',
              borderRight: '2px solid #25D366',
              fontSize: '11px',
              color: '#333',
              maxWidth: '300px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block'
            }}>
              ({formatDateTimeHe(lastCustomerMessage.timestamp, { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}) {lastCustomerMessage.body?.substring(0, 60) || '(מדיה)'}{lastCustomerMessage.body?.length > 60 ? '...' : ''}
            </span>
          )}
        </div>
      )}

      {/* Messages Area */}
      <div ref={messagesAreaRef} style={{
        flex: 1,
        padding: '12px',
        overflowY: 'auto',
        background: '#e5ddd5',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>טוען הודעות...</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <span style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }}>💬</span>
            <p>אין הודעות קודמות</p>
            <p style={{ fontSize: '13px' }}>שלח הודעה ראשונה ללקוח</p>
          </div>
        ) : (
          Object.entries(messageGroups).map(([date, msgs]) => (
            <React.Fragment key={date}>
              {/* Date Separator */}
              <div style={{
                textAlign: 'center',
                margin: '8px 0'
              }}>
                <span style={{
                  background: 'rgba(225, 218, 208, 0.9)',
                  padding: '6px 16px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#54656f',
                  boxShadow: '0 1px 1px rgba(0,0,0,0.1)'
                }}>
                  {date}
                </span>
              </div>

              {/* Messages for this date */}
              {msgs.map((msg, idx) => {
                // PR #800 owner-spec (#4/#5 v2 2026-06-08): a system-level
                // marker (e.g. "{user} ended session at {time}") is logged
                // locally only — render as a centered date-style badge,
                // matching the date separator above.
                if (msg.kind === 'system_marker') {
                  // Chat store envelope uses `body` for the text payload
                  // (same as every other rendering path here). PR #800
                  // shipped reading `msg.message` — hotfix.
                  return (
                    <div
                      key={msg.id || idx}
                      ref={el => { if (msg.id) messageRefs.current[msg.id] = el; }}
                      style={{ textAlign: 'center', margin: '8px 0', alignSelf: 'stretch' }}
                    >
                      <span style={{
                        background: 'rgba(225, 218, 208, 0.9)',
                        padding: '6px 16px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#54656f',
                        boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                        display: 'inline-block',
                        maxWidth: '85%',
                      }}>
                        {msg.body || msg.message || ''}
                      </span>
                    </div>
                  );
                }
                const isCurrentMatch = chatSearchMatches[chatSearchMatchIdx] === msg.id;
                const isAnyMatch = chatSearchMatches.includes(msg.id);
                return (
                <div
                  key={msg.id || idx}
                  ref={el => { if (msg.id) messageRefs.current[msg.id] = el; }}
                  style={{
                    maxWidth: '70%',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    boxShadow: isCurrentMatch
                      ? '0 0 0 3px #ffc107'
                      : isAnyMatch
                        ? '0 0 0 2px rgba(255,193,7,0.45)'
                        : '0 1px 1px rgba(0,0,0,0.1)',
                    alignSelf: msg.fromMe ? 'flex-end' : 'flex-start',
                    background: msg.fromMe
                      ? (msg.channel === 'sms' ? '#e8e0f4' : '#dcf8c6')
                      : 'white',
                    borderTopLeftRadius: msg.fromMe ? '8px' : '2px',
                    borderTopRightRadius: msg.fromMe ? '2px' : '8px',
                    transition: 'box-shadow 0.18s ease',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    const ic = e.currentTarget.querySelector('.wa-actions-trigger');
                    if (ic) ic.style.opacity = '1';
                  }}
                  onMouseLeave={(e) => {
                    const ic = e.currentTarget.querySelector('.wa-actions-trigger');
                    if (ic) ic.style.opacity = '0';
                  }}
                >
                  {/* Phase 3 owner-spec 2026-05-31: action dropdown */}
                  <MessageActionsMenu
                    msg={msg}
                    align={msg.fromMe ? 'right' : 'left'}
                    onReply={() => setReplyTarget(buildReplyTarget(msg))}
                    onInfo={() => setInfoModalMsg(msg)}
                  />
                  {msg.replyTo && (
                    <ReplyQuote
                      replyTo={msg.replyTo}
                      messages={messages}
                      onClickOriginal={scrollToMessage}
                    />
                  )}
                  {renderMessageContent(msg)}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: '4px',
                    fontSize: '11px',
                    color: '#999',
                    justifyContent: 'flex-end'
                  }}>
                    {msg.channel === 'sms' && (
                      <span style={{
                        background: '#5856D6',
                        color: 'white',
                        padding: '1px 5px',
                        borderRadius: '6px',
                        fontSize: '9px',
                        fontWeight: '600'
                      }}>SMS</span>
                    )}
                    {msg.sentByName && <span style={{ color: msg.channel === 'sms' ? '#5856D6' : '#25D366', fontWeight: '500' }}>{msg.sentByName}</span>}
                    <span>{formatTime(msg.timestamp)}</span>
                    {getStatusIcon(msg)}
                  </div>
                </div>
                );
              })}
            </React.Fragment>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          padding: '8px 12px',
          background: '#ffebee',
          color: '#c62828',
          fontSize: '13px',
          textAlign: 'center'
        }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginRight: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Input Area */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 12px',
        background: '#f0f2f5',
        borderRadius: '0 0 8px 8px',
        borderTop: '1px solid #e0e0e0'
      }}>
        {/* Templates */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => { setShowTemplates(!showTemplates); setShowEmojis(false); setShowAttachMenu(false); }}
            style={{
              width: '36px',
              height: '36px',
              background: 'transparent',
              border: 'none',
              borderRadius: '50%',
              fontSize: '18px',
              cursor: 'pointer'
            }}
            title="תבניות הודעה"
          >
            📝
          </button>
          {showTemplates && (
            <div style={{
              position: 'absolute',
              bottom: '45px',
              right: 0,
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              width: '220px',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 100
            }}>
              <div style={{ padding: '12px', background: '#25D366', color: 'white', fontWeight: '600', textAlign: 'center' }}>
                תבניות הודעה
              </div>
              {templates.length === 0 ? (
                <div style={{ padding: 14, fontSize: 12, color: '#888', textAlign: 'center' }}>
                  אין תבניות פעילות. ניתן להוסיף ולהפעיל ב-הגדרות → תבניות הודעות WhatsApp.
                </div>
              ) : templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    borderBottom: '1px solid #f0f0f0',
                    background: 'white',
                    cursor: 'pointer',
                    textAlign: 'right',
                    fontSize: '13px'
                  }}
                >
                  {template.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Emojis */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => { setShowEmojis(!showEmojis); setShowTemplates(false); setShowAttachMenu(false); }}
            style={{
              width: '36px',
              height: '36px',
              background: 'transparent',
              border: 'none',
              borderRadius: '50%',
              fontSize: '18px',
              cursor: 'pointer'
            }}
            title="אימוג'ים"
          >
            😊
          </button>
          {showEmojis && (
            <div style={{
              position: 'absolute',
              bottom: '45px',
              right: 0,
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              padding: '12px',
              width: '220px',
              zIndex: 100
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '4px' }}>
                {QUICK_EMOJIS.map((emoji, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectEmoji(emoji)}
                    style={{
                      width: '36px',
                      height: '36px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '20px',
                      cursor: 'pointer'
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Attachments */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => { setShowAttachMenu(!showAttachMenu); setShowTemplates(false); setShowEmojis(false); }}
            style={{
              width: '36px',
              height: '36px',
              background: 'transparent',
              border: 'none',
              borderRadius: '50%',
              fontSize: '18px',
              cursor: 'pointer'
            }}
            title="צרף קובץ"
          >
            📎
          </button>
          {showAttachMenu && (
            <div style={{
              position: 'absolute',
              bottom: '45px',
              right: 0,
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              zIndex: 100
            }}>
              <button
                onClick={() => imageInputRef.current?.click()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'white',
                  width: '100%',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🖼️ תמונה
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'white',
                  width: '100%',
                  cursor: 'pointer',
                  fontSize: '14px',
                  borderTop: '1px solid #f0f0f0'
                }}
              >
                📄 קובץ
              </button>
            </div>
          )}
        </div>

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={imageInputRef}
          onChange={(e) => handleFileSelect(e, 'image')}
          accept="image/*"
          style={{ display: 'none' }}
        />
        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => handleFileSelect(e, 'document')}
          style={{ display: 'none' }}
        />

        {/* Phase 2 owner-spec 2026-05-31: reply composer bar */}
        <ReplyComposerBar replyTarget={replyTarget} onCancel={() => setReplyTarget(null)} />

        {/* Message Input */}
        <input
          type="text"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="הקלד הודעה..."
          disabled={uploadingFile}
          style={{
            flex: 1,
            padding: '10px 16px',
            border: 'none',
            borderRadius: '20px',
            fontSize: '14px',
            background: 'white',
            outline: 'none'
          }}
        />

        {/* SMS Button */}
        <button
          onClick={() => handleSendMessage('sms')}
          disabled={!messageText.trim() || sending || uploadingFile}
          title="SMS"
          style={{
            width: '40px',
            height: '40px',
            background: '#5856D6',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: !messageText.trim() || sending || uploadingFile ? 0.5 : 1
          }}
        >
          SMS
        </button>

        {/* WhatsApp Send Button */}
        <button
          onClick={() => handleSendMessage('whatsapp')}
          disabled={!messageText.trim() || sending || uploadingFile}
          title="WhatsApp"
          style={{
            width: '40px',
            height: '40px',
            background: '#25D366',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: !messageText.trim() || sending || uploadingFile ? 0.5 : 1
          }}
        >
          {uploadingFile ? '⏳' : sending ? '...' : '◀'}
        </button>
      </div>

      {/* Phase 1.1 owner-spec 2026-05-31: shared media overlays —
          same shells used by WhatsAppChat.js + ChatModal. */}
      {lightbox.open && lightboxMedia.length > 0 && (
        <MediaLightbox
          mediaList={lightboxMedia}
          currentIndex={lightbox.index}
          onClose={() => setLightbox({ open: false, index: 0 })}
          onPrev={() => setLightbox(l => ({ ...l, index: Math.max(0, l.index - 1) }))}
          onNext={() => setLightbox(l => ({ ...l, index: Math.min(lightboxMedia.length - 1, l.index + 1) }))}
          onJumpTo={(i) => setLightbox({ open: true, index: i })}
        />
      )}
      <MediaLibraryDrawer
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        messages={messages}
        onMediaClick={(i) => {
          setLibraryOpen(false);
          setLightbox({ open: true, index: i });
        }}
      />
      {/* Phase 3: message-info modal */}
      <MessageInfoModal msg={infoModalMsg} onClose={() => setInfoModalMsg(null)} />
    </div>
  );
};

export default RecordWhatsAppChat;
